import { useRevalidator } from "react-router-dom";
import { Avatar } from "./Avatar";
import { DateRangeFilter } from "./DateRangeFilter";
import { FilterMenu, alphabetical } from "./FilterMenu";
import { RequestList } from "./RequestList";
import type { ListSort } from "./RequestList";
import { updateRequest } from "../data/api";
import { useRoster } from "../data/RosterContext";
import { ownerGroups } from "../data/requestGroups";
import { useTags } from "../data/TagsContext";
import { NO_TECH_OWNER, STATUS_META, STATUS_ORDER } from "../data/status";
import { useSearchFilter } from "../data/useSearchFilter";
import type { Account, RequestSummary } from "../data/types";

/**
 * The staff request table with its filter bar, as the Requests page shows it.
 * Every filter lives in the URL, so the page's loader refetches from there.
 * `lockedAccount` pins the Account filter to one account and disables it —
 * the account page's view, where the loader applies that account itself.
 */
export function RequestsTable({ requests, accounts, lockedAccount, groupBy = "status" }: { requests: RequestSummary[]; accounts: Account[]; lockedAccount?: Account; groupBy?: "status" | "owner" }) {
  const { members } = useRoster();
  const { tags } = useTags();
  const [params, setFilter, setFilters] = useSearchFilter();
  const sortParam = params.get("sort");
  const sort: ListSort = sortParam === "next_asc" || sortParam === "next_desc" ? sortParam : null;
  const revalidator = useRevalidator();
  const people = alphabetical(members.map((o) => ({ value: o.id, label: o.name, leading: <Avatar owner={o} size="sm" /> })));

  // Grouping by owner while filtering to one owner would otherwise draw five
  // empty headings and an empty Unassigned. Narrow the roster to the filtered
  // person and drop Unassigned, which an owner filter excludes by definition —
  // except "No technical owner", which is Unassigned and nothing else.
  const ownerFilter = params.get("owner");
  const noOwner = ownerFilter === NO_TECH_OWNER;
  const group =
    groupBy === "owner"
      ? ownerGroups(noOwner ? [] : ownerFilter ? members.filter((m) => m.id === ownerFilter) : members, { includeUnassigned: !ownerFilter || noOwner })
      : undefined;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterMenu
          label="Status"
          value={params.get("status") ?? ""}
          onSelect={(v) => setFilter("status", v)}
          options={[
            { value: "", label: "All statuses" },
            { value: "open", label: "Open" },
            ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label })),
            { value: "archived", label: "Archived" },
          ]}
        />
        {lockedAccount ? (
          <FilterMenu label="Account" value={lockedAccount.id} onSelect={() => undefined} options={[{ value: lockedAccount.id, label: lockedAccount.name }]} lockedReason="This page shows one account's requests" />
        ) : (
          <FilterMenu
            label="Account"
            value={params.get("account") ?? ""}
            onSelect={(v) => setFilter("account", v)}
            options={[{ value: "", label: "All accounts" }, ...alphabetical(accounts.map((a) => ({ value: a.id, label: a.name })))]}
          />
        )}
        <FilterMenu label="Account lead" value={params.get("lead") ?? ""} onSelect={(v) => setFilter("lead", v)} options={[{ value: "", label: "Anyone" }, ...people]} />
        <FilterMenu
          label="Technical owner"
          value={params.get("owner") ?? ""}
          onSelect={(v) => setFilter("owner", v)}
          options={[{ value: "", label: "Anyone" }, { value: NO_TECH_OWNER, label: "No technical owner" }, ...people]}
        />
        <FilterMenu
          label="Priority"
          value={params.get("priority") ?? ""}
          onSelect={(v) => setFilter("priority", v)}
          options={[
            { value: "", label: "Any priority" },
            ...[0, 1, 2, 3, 4].map((p) => ({ value: String(p), label: p === 0 ? "P0 · Urgent" : `P${p}` })),
          ]}
        />
        <FilterMenu
          label="Tag"
          value={params.get("tag") ?? ""}
          onSelect={(v) => setFilter("tag", v)}
          options={[{ value: "", label: "Any tag" }, ...alphabetical(tags.map((t) => ({ value: t.id, label: t.hidden ? `${t.name} (hidden)` : t.name })))]}
        />
        <DateRangeFilter label="Created" from={params.get("created_from") ?? ""} to={params.get("created_to") ?? ""} onChange={(r) => setFilters({ created_from: r.from, created_to: r.to })} />
        <DateRangeFilter label="Needed by" from={params.get("needed_from") ?? ""} to={params.get("needed_to") ?? ""} onChange={(r) => setFilters({ needed_from: r.from, needed_to: r.to })} />
        <DateRangeFilter label="Updated" from={params.get("updated_from") ?? ""} to={params.get("updated_to") ?? ""} onChange={(r) => setFilters({ updated_from: r.from, updated_to: r.to })} />
        <span className="ml-auto text-xs text-[var(--text-2)]">
          {requests.length} {requests.length === 1 ? "request" : "requests"}
        </span>
      </div>

      <RequestList
        variant="staff"
        rows={requests}
        group={group}
        sort={sort}
        onSort={(next) => setFilter("sort", next ?? "")}
        onOrderChange={async (id, order) => {
          await updateRequest(id, { order });
          revalidator.revalidate();
        }}
        // Carry where the row was clicked from, so the detail's back link
        // returns here rather than to every customer's requests. In the URL,
        // not router state, so it survives a refresh — the moment it silently
        // does not is the moment someone is screen-sharing.
        linkTo={(id) => `/requests/${id}${lockedAccount ? "?from=account" : ""}`}
      />
    </>
  );
}
